import {
  QuestionnaireCategoryItemsItemOneOf as Question,
  QuestionnaireResponseQuestionResponseCurrentAnswer,
  UpdateHealthDataProfileResponse,
} from '@/api/generated-catch-backend/model';
import { useGetHealthData, useUpdateHealthDataPoint } from '@/app/components/health-data/use-health-data';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { QuestionnaireProvider } from '../../context/QuestionnaireContext';
import { extractAnswerValue } from '../../hooks/use-questionnaire-form';
import { createQuestionSchema } from '../../schemas/questionnaire-schema';
import { QuestionnaireCard } from '../questionnaire-card/questionnaire-card';
import { Header } from '../questionnaire-header/header';
import { QuestionnaireWrapper } from '../questionnaire-wrapper';
import { BaseNextButton } from '../survey-item/components/next-button/base-next-button';

const getDefaultValueForQuestion = (
  question: Question,
  currentAnswer: QuestionnaireResponseQuestionResponseCurrentAnswer,
) => {
  if (!currentAnswer) return null;

  const extractedValue = extractAnswerValue(currentAnswer.value);

  if (question.questionType === 'select' || question.questionType === 'radio') {
    return Array.isArray(extractedValue) && extractedValue.length > 0 ? extractedValue[0] : '';
  } else if (question.questionType === 'multiselect' || question.questionType === 'checkbox') {
    return Array.isArray(extractedValue) ? extractedValue : [];
  }

  // For all other question types, return the extracted value as-is
  return extractedValue;
};

interface QuestionnaireEditModalContentProps {
  currentAnswer: QuestionnaireResponseQuestionResponseCurrentAnswer | null;
  question: Question;
  healthDataPointSlug: string;
  title?: string;
  subtitle?: string;
  onClose?: (response?: UpdateHealthDataProfileResponse) => void;
  isTransitioningToNext?: boolean;
}

function QuestionnaireEditModalContent({
  currentAnswer,
  question,
  healthDataPointSlug,
  title = 'Edit Health Data',
  subtitle,
  onClose,
  isTransitioningToNext = false,
}: QuestionnaireEditModalContentProps) {
  const [isWhyWeAskExpanded, setIsWhyWeAskExpanded] = useState(false);
  const { updateHealthDataPoint, isPending } = useUpdateHealthDataPoint();
  const { isFetching } = useGetHealthData();

  // Button stays loading during mutation OR while transitioning to next question
  const isButtonLoading = isPending || isFetching || isTransitioningToNext;

  // Create validation schema for this single question
  const questionSchema = createQuestionSchema(question);
  const formSchema = z.object({
    [question.slug]: questionSchema,
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      [question.slug]: getDefaultValueForQuestion(question, currentAnswer),
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const answerValue = data[question.slug];

    // Skip update if value is null or undefined
    if (answerValue === null || answerValue === undefined) {
      console.error('No answer value provided');
      return;
    }

    updateHealthDataPoint({
      healthDataPointSlug,
      value: answerValue,
      question,
      onSuccess: (response) => {
        onClose?.(response);
      },
      onError: (error) => {
        console.error('Failed to update health data:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update. Please try again.';
        form.setError(question.slug, {
          type: 'backend',
          message: errorMessage,
        });
      },
    });
  });

  const handleNext = async () => {
    const isValid = await form.trigger(question.slug);
    if (isValid) {
      handleSubmit();
    }
  };

  // Wrap onClose to ensure it's called without arguments (not with a click event)
  // This signals a user cancellation vs a successful update
  const handleClose = () => onClose?.();

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <QuestionnaireWrapper className="questionnaire-wrapper inset-0 h-screen overflow-hidden">
          <Header
            categoryName={title || 'Edit Health Data'}
            subCategoryName={subtitle}
            onBack={handleClose}
            showProgress={false}
            showCloseIcon={true}
            onClose={handleClose}
            isBackDisabled={false}
          />
          <QuestionnaireCard
            isWhyWeAskExpanded={isWhyWeAskExpanded}
            setIsWhyWeAskExpanded={setIsWhyWeAskExpanded}
            currentQuestion={question}
            handleNext={handleNext}
          />
          {!isWhyWeAskExpanded && (
            <div className="fixed-bottom-controls">
              <div className="controls-container">
                {form.formState.errors[question.slug] && (
                  <div className="w-full px-4 pb-2">
                    <p className="text-center text-sm text-red-500">
                      {form.formState.errors[question.slug]?.message as string}
                    </p>
                  </div>
                )}
                <div className="h-8"></div>
              </div>

              <div className="btn-primary-container">
                <BaseNextButton text="Update" disabled={isButtonLoading} onNext={() => handleNext()} />
              </div>
            </div>
          )}
        </QuestionnaireWrapper>
      </form>
    </FormProvider>
  );
}

interface QuestionnaireEditModalProps {
  currentAnswer: QuestionnaireResponseQuestionResponseCurrentAnswer | null;
  question: Question;
  healthDataPointSlug: string;
  title?: string;
  subtitle?: string;
  onClose?: (response?: UpdateHealthDataProfileResponse) => void;
  isOpen?: boolean;
  isTransitioningToNext?: boolean;
}

export function QuestionnaireEditModal({
  currentAnswer,
  question,
  healthDataPointSlug,
  title,
  subtitle,
  onClose,
  isOpen = true,
  isTransitioningToNext = false,
}: QuestionnaireEditModalProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <QuestionnaireProvider>
        <QuestionnaireEditModalContent
          currentAnswer={currentAnswer}
          question={question}
          healthDataPointSlug={healthDataPointSlug}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          isTransitioningToNext={isTransitioningToNext}
        />
      </QuestionnaireProvider>
    </div>
  );
}
